<template>
  <form @submit="onSubmit">
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="label"
        :label="t('operationspecification.detail.label')"
        :placeholder="t('operationspecification.detail.label')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsTextAreaField
        name="description"
        :label="t('operationspecification.detail.description')"
        :placeholder="t('operationspecification.detail.description')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="schema"
        :label="t('operationspecification.detail.schema')"
        :placeholder="t('operationspecification.detail.schema')"
      />
      <vl-alert
        v-if="data.schemaNotUnique"
        icon="warning"
        :content="t('operationspecification.detail.schemaNotUnique')"
        mod-warning
        mod-naked
      />
    </vl-form-column>
    <vl-action-group v-vl-spacer:bottom.small>
      <vl-button icon="save" :mod-loading="reloading" mod-icon-before type="submit">
        {{ $t('general.save') }}
      </vl-button>
      <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeCreate">
        {{ $t('general.cancel') }}
      </vl-button>
    </vl-action-group>
  </form>
</template>
<script lang="ts" setup>
import { useOperationSpecificationStore } from '@/modules/configuration/store/operation-specification/operation-specification.store';
import { useDeliveryProcessDefinitionEditStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-edit.store';
import { useForm } from 'vee-validate';
import { debounce } from 'lodash';
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { object, string } from 'yup';
import PbsInputField from '@/components/forms/PbsInputField.vue';
import PbsTextAreaField from '@/components/forms/PbsTextAreaField.vue';
import { RegularExpressions, noSurroundingSpaces } from '@/helpers';

const store = useOperationSpecificationStore();
const editStore = useDeliveryProcessDefinitionEditStore();

const { t } = useI18n();
const route = useRoute();

const data = reactive({
  schemaNotUnique: false
});

const validationSchema = object({
  label: string()
    .required(t('validations.messages.required', { _field_: t('operationspecification.detail.label') }))
    .test(noSurroundingSpaces(t('operationspecification.detail.label')))
    .max(256, t('validations.messages.maxChar', { _field_: t('operationspecification.detail.label'), length: 256 })),
  description: string().required(
    t('validations.messages.required', { _field_: t('operationspecification.detail.description') })
  ),
  schema: string()
    .required(t('validations.messages.required', { _field_: t('deliveryspecification.detail.schema') }))
    .matches(
      RegularExpressions.noSpaces,
      t('validations.messages.noSpaces', { _field_: t('deliveryspecification.detail.schema') })
    )
});

const { handleSubmit, values } = useForm({
  validationSchema
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const definitionId = computed((): number => {
  return parseInt(route.params.id as string, 10);
});

watch(
  () => values.schema,
  (newValue) => {
    data.schemaNotUnique = false;
    const trimmedSchema = newValue?.trim();
    if (trimmedSchema.length > 1) {
      checkSchemaUnique(trimmedSchema);
    }
  }
);

const checkSchemaUnique = debounce(async (schema: string) => {
  if (schema && schema.trim().length > 0) {
    data.schemaNotUnique = !(await store.hasUniqueSchema(schema.trim()));
  } else {
    data.schemaNotUnique = false;
  }
}, 250);

const onSuccess = async (values) => {
  await store.create(definitionId.value, {
    deliveryProcessDefinitionId: definitionId.value,
    label: values.label,
    description: values.description,
    schema: values.schema
  });
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>

<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
</style>
